import _ from "lodash";

const CERTIFICATE_STORAGE_KEY_PATTERN = /^\d+_Certificate_\d+$/;

function getISBN(select) {
  return select.dataset.isbn;
}

export function removeStoredCertificatesByGuid(guid) {
  const guids = new Set(
    String(guid)
      .split(";")
      .map((g) => g.trim())
      .filter(Boolean)
  );

  if (!guids.size) return;

  const keysToRemove = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!CERTIFICATE_STORAGE_KEY_PATTERN.test(key)) continue;

    try {
      const parsed = JSON.parse(localStorage.getItem(key));
      if (parsed?.guid && guids.has(parsed.guid)) {
        keysToRemove.push(key);
      }
    } catch (e) {}
  }

  keysToRemove.forEach((key) => localStorage.removeItem(key));
}

function getCertificateNumbers(isbn) {
  const prefix = `${isbn}_Certificate_`;
  const certs = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);

    if (key.startsWith(prefix)) {
      const num = parseInt(key.replace(prefix, ""), 10);
      if (!isNaN(num)) {
        certs.push(num);
      }
    }
  }

  certs.sort((a, b) => a - b);
  const nextIndex = certs.length ? Math.max(...certs) + 1 : 1;
  certs.push(nextIndex);

  return certs;
}

export function populateCertificateSelect(select) {
  const isbn = getISBN(select);
  const certNumbers = getCertificateNumbers(isbn);

  select.innerHTML = "";

  certNumbers.forEach((num, index) => {
    const option = document.createElement("option");
    option.value = num;
    option.textContent = `Certificate ${num}`;

    if (index === certNumbers.length - 1) {
      option.selected = true;
    }

    select.appendChild(option);
  });
}

export function getCertificateKey(select) {
  if (!select) return;

  const number = select.selectedIndex + 1;
  const isbn = select.dataset.isbn;

  if (!number && !isbn) return;

  return `${isbn}_Certificate_${number}`;
}

export function serializeForm(form) {
  if (!form) return;

  const data = {};

  form.querySelectorAll("input, select").forEach((el) => {
    if (!el.name && !el.id) return;

    const key = el.name || el.id;
    data[key] = el.value;
  });

  return data;
}

export function trimRequiredFormFields(form) {
  if (!form) return;

  form.querySelectorAll("input[required]").forEach((el) => {
    if (el.type === "hidden" || el.type === "number") return;
    el.value = el.value.trim();
  });
}

export function populateForm(form, data) {
  if (!form) return;

  form.querySelectorAll("input, select").forEach((el) => {
    const key = el.name || el.id;

    if (!data[key]) {
      return;
    }

    el.value = data[key];

    if (el.tagName === "SELECT") {
      const input = el
        .closest(".form-row")
        ?.querySelector("input[type='text']");
      if (input) input.style.fontFamily = el.value;
    }
  });
}

export function isPreviewStillValid(url) {
  const params = new URL(url).searchParams;

  const amzDate = params.get("X-Amz-Date");
  const expires = parseInt(params.get("X-Amz-Expires"), 10);

  const start = new Date(
    amzDate.replace(
      /(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z/,
      "$1-$2-$3T$4:$5:$6Z"
    )
  );

  const expiration = new Date(start.getTime() + expires * 1000);

  return expiration > new Date();
}

export const togglePreviewLoader = (isActive) => {
  const previewLoader = document.querySelector(".preview-loader");
  if (!previewLoader) return;

  document.body.classList.toggle("preview-loader-active", isActive);
  previewLoader.classList.toggle("active", isActive);
};

export async function createAndRetrievePDF(
  type = "preview",
  dataStored = {},
  guid = ""
) {
  const { url, token } = certAPI;
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Basic ${token}`
  };

  togglePreviewLoader(true);

  //set PDF type
  dataStored.type = type;
  if (guid) dataStored.guid = guid;

  try {
    if (!guid) {
      const createRes = await fetch(`${url}/create-pdf`, {
        method: "POST",
        headers,
        body: JSON.stringify(dataStored)
      });

      if (!createRes.ok) {
        throw new Error(`Create failed: ${createRes.status}`);
      }

      const createData = await createRes.json();
      guid = createData.guid;
    }

    const retrieveRes = await fetch(`${url}/retrieve-pdf`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        type,
        guid: guid
      })
    });

    if (!retrieveRes.ok) {
      togglePreviewLoader(false);
      throw new Error(`Retrieve failed: ${retrieveRes.status}`);
    }

    const retrieveData = await retrieveRes.json();

    return {
      preview: retrieveData?.downloadUrl,
      guid
    };
  } catch (err) {
    console.error("Request failed:", err);
    togglePreviewLoader(false);
  } finally {
    togglePreviewLoader(false);
  }
}

/**
 * Selected slot saved, previewed (has guid), and form matches storage.
 */
export function isCurrentCertificateCheckoutReady(
  getStored,
  certificateSelect,
  form
) {
  const raw = getStored(getCertificateKey(certificateSelect));
  if (!raw) return false;

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    return false;
  }

  if (!parsed?.guid) return false;

  const { preview, guid, type, ...savedPayload } = parsed;
  return _.isEqual(savedPayload, serializeForm(form));
}

/**
 * GUIDs for checkout URL. Empty unless the selected certificate is ready; then
 * all slots with a guid whose data is still valid (active slot must match form).
 */
export function getEligibleGuidsForCheckout(
  getStored,
  currentISBN,
  certificateSelect,
  form
) {
  if (!isCurrentCertificateCheckoutReady(getStored, certificateSelect, form)) {
    return [];
  }

  const prefix = `${currentISBN}_Certificate_`;
  const currentKey = getCertificateKey(certificateSelect);
  const currentData = serializeForm(form);
  const guids = [];

  let i = 1;
  while (true) {
    const raw = getStored(`${prefix}${i}`);
    if (!raw) break;

    try {
      const parsed = JSON.parse(raw);
      if (!parsed?.guid) {
        i++;
        continue;
      }

      const { preview, guid, type, ...savedPayload } = parsed;
      const key = `${prefix}${i}`;

      if (key === currentKey) {
        if (!_.isEqual(savedPayload, currentData)) {
          i++;
          continue;
        }
      }

      guids.push(parsed.guid);
    } catch (e) {}

    i++;
  }

  return [...new Set(guids)];
}

/**
 * True when the form differs from what is saved for the selected certificate
 * (ignoring preview / guid / type).
 *
 */
export function checkFormValuesDiffer(
  getStored,
  certificateSelect,
  form,
  currentData = serializeForm(form)
) {
  const raw = getStored(getCertificateKey(certificateSelect));
  if (!raw) return true;

  let savedRaw;
  try {
    savedRaw = JSON.parse(raw);
  } catch (e) {
    return true;
  }
  if (!savedRaw) return true;

  const { preview, guid, type, ...resetData } = savedRaw;

  return !_.isEqual(resetData, currentData);
}

export function updateCertificateBuyLink(
  buyBtn,
  getStored,
  currentISBN,
  certificateSelect,
  form
) {
  if (!buyBtn) return;

  const url = new URL(buyBtn.href);

  const guids = getEligibleGuidsForCheckout(
    getStored,
    currentISBN,
    certificateSelect,
    form
  );

  if (guids.length) {
    url.searchParams.set("GUID", guids.join(";"));
  } else {
    url.searchParams.delete("GUID");
  }

  buyBtn.href = url.toString();

  document.dispatchEvent(new Event("certificate-guid-updated"));
}
